import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Search,
  User,
  Navigation as NavigationIcon,
  Leaf,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Calendar,
  X,
  MapPin,
  FileSpreadsheet,
  Receipt,
  FileText,
  Layers
} from 'lucide-react'
import toast from 'react-hot-toast'
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks } from 'date-fns'
import { sv } from 'date-fns/locale'
import { supabase } from '../../lib/supabase'
import {
  getAssignmentStatusBadgeClassesV2,
  getAssignmentStatusNameV2,
  ASSIGNMENT_STATUSES_V2
} from '../../services/statusSystemV2'
import { calculateAssignmentStatusV2 } from '../../services/assignmentStatusCalculatorV2'
import type { AssignmentStatusV2 } from '../../types/statusTypes'
import { ExportCustomerInvoiceModal } from '../../components/ExportCustomerInvoiceModal'
import { ExportTgInvoiceAdminModal } from '../../components/ExportTgInvoiceAdminModal'

interface Assignment {
  id: string
  assignment_number: number | null
  title: string
  description: string
  status: string
  start_date: string
  start_time: string
  end_time: string
  location: string | { address: string; coordinates?: { lat: number; lng: number } }
  recurring_type: string
  estimated_hours: number
  customer_id: string
  customer_name: string
  subcontractors: Array<{ id: string; name: string; tg_status?: string; tg_invoice_status?: string }>
  customer_comm_status: string
  customer_invoice_status: string
  created_at: string
  updated_at: string
}



export function AdminAssignments() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter] = useState('all') // Legacy, kept for filtering logic
  const [itemsPerPage, setItemsPerPage] = useState(20)

  // Sorting state
  const [sortField, setSortField] = useState<string>('start_date')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  // URL params for direct filtering (e.g., from CustomerCard)
  const urlCustomerId = searchParams.get('customer')

  // Load saved filter state from sessionStorage
  const getSavedFilters = () => {
    try {
      const saved = sessionStorage.getItem('assignmentFilters')
      if (saved) {
        return JSON.parse(saved)
      }
    } catch (e) {
      console.error('Error loading saved filters:', e)
    }
    return null
  }
  const savedFilters = getSavedFilters()

  // Advanced filter state - restore from sessionStorage if available
  // If URL has customer param, use that and show filters
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(urlCustomerId ? true : (savedFilters?.showAdvancedFilters ?? false))
  const [dateFrom, setDateFrom] = useState<string>(savedFilters?.dateFrom ?? '')
  const [dateTo, setDateTo] = useState<string>(savedFilters?.dateTo ?? '')
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(savedFilters?.selectedStatuses ?? [])
  const [selectedSubcontractors, setSelectedSubcontractors] = useState<string[]>(savedFilters?.selectedSubcontractors ?? [])
  const [selectedTgStatuses, setSelectedTgStatuses] = useState<string[]>(savedFilters?.selectedTgStatuses ?? [])
  const [selectedCustomerStatuses, setSelectedCustomerStatuses] = useState<string[]>(savedFilters?.selectedCustomerStatuses ?? [])
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>(urlCustomerId ? [urlCustomerId] : (savedFilters?.selectedCustomers ?? []))
  const [selectedCities, setSelectedCities] = useState<string[]>(savedFilters?.selectedCities ?? [])

  // Invoice filter state
  const [filterReadyForCustomerInvoice, setFilterReadyForCustomerInvoice] = useState(savedFilters?.filterReadyForCustomerInvoice ?? false)
  const [filterReadyForTgInvoice, setFilterReadyForTgInvoice] = useState(savedFilters?.filterReadyForTgInvoice ?? false)

  // Export modal state
  const [showCustomerExportModal, setShowCustomerExportModal] = useState(false)
  const [showTgExportModal, setShowTgExportModal] = useState(false)

  // Search within filter lists
  const [customerSearch, setCustomerSearch] = useState('')
  const [subcontractorSearch, setSubcontractorSearch] = useState('')
  const [citySearch, setCitySearch] = useState('')

  // Save filter state to sessionStorage when it changes
  useEffect(() => {
    const filterState = {
      showAdvancedFilters,
      dateFrom,
      dateTo,
      selectedStatuses,
      selectedSubcontractors,
      selectedTgStatuses,
      selectedCustomerStatuses,
      selectedCustomers,
      selectedCities,
      filterReadyForCustomerInvoice,
      filterReadyForTgInvoice
    }
    sessionStorage.setItem('assignmentFilters', JSON.stringify(filterState))
  }, [showAdvancedFilters, dateFrom, dateTo, selectedStatuses, selectedSubcontractors, selectedTgStatuses, selectedCustomerStatuses, selectedCustomers, selectedCities, filterReadyForCustomerInvoice, filterReadyForTgInvoice])

  // Handle URL customer param - clear date filters and URL after applying
  useEffect(() => {
    if (urlCustomerId) {
      // Clear date filters to show all assignments for this customer
      setDateFrom('')
      setDateTo('')
      // Clear URL param after applying (prevents issues on page refresh)
      setSearchParams({}, { replace: true })
    }
  }, []) // Only run once on mount

  // Reset form to initial state

  // Helper functions for location handling
  const getLocationDisplay = (location: string | { address: string; coordinates?: { lat: number; lng: number } }) => {
    return typeof location === 'string' ? location : location.address
  }

  const getLocationCoordinates = (location: string | { address: string; coordinates?: { lat: number; lng: number } }) => {
    return typeof location === 'object' ? location.coordinates : undefined
  }

  // Fetch assignments from Supabase
  const { data: assignments = [], isLoading, error } = useQuery({
    queryKey: ['assignments'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('assignments')
        .select(`
          *,
          customers!inner(id, full_name, address_street, address_city),
          assignment_subcontractors(
            subcontractor_id,
            tg_status,
            tg_invoice_status,
            subcontractors(id, name)
          )
        `)
        .order('start_date', { ascending: false })

      if (error) {
        console.error('Error fetching assignments:', error)
        throw error
      }

      return data.map(assignment => {
        const subcontractors = assignment.assignment_subcontractors || [];
        const subcontractorIds = subcontractors.map((as: any) => as.subcontractor_id).filter(Boolean);

        // Use the status calculator to get the correct status
        const calculatedStatus = calculateAssignmentStatusV2({
          status: assignment.status as AssignmentStatusV2,
          start_date: assignment.start_date || '',
          start_time: assignment.start_time || '',
          subcontractor_ids: subcontractorIds
        });

        return {
          id: assignment.id,
          assignment_number: assignment.assignment_number,
          title: assignment.title,
          description: assignment.description || '',
          status: calculatedStatus,
          start_date: assignment.start_date,
          start_time: assignment.start_time,
          end_time: assignment.end_time,
          location: assignment.location || '',
          recurring_type: assignment.recurring_type,
          estimated_hours: assignment.estimated_hours,
          customer_id: assignment.customer_id,
          customer_name: assignment.customers?.full_name || 'Okänd kund',
          subcontractors: assignment.assignment_subcontractors
            ?.filter((as: any) => as.tg_status !== 'avsagt')
            .map((as: any) => ({
              id: as.subcontractor_id,
              name: as.subcontractors?.name,
              tg_status: as.tg_status,
              tg_invoice_status: as.tg_invoice_status
            })).filter((s: any) => s.id && s.name) || [],
          customer_comm_status: assignment.customer_comm_status || 'ej_kontaktad',
          customer_invoice_status: assignment.customer_invoice_status || 'ej_fakturerad',
          created_at: assignment.created_at,
          updated_at: assignment.updated_at
        }
      })
    }
  })


  // Fetch customers from Supabase
  const { data: customers = [] } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .order('full_name')

      if (error) {
        console.error('Error fetching customers:', error)
        return []
      }

      return data
    }
  })

  // Fetch subcontractors from Supabase
  const { data: subcontractors = [] } = useQuery({
    queryKey: ['subcontractors'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subcontractors')
        .select('*')
        .order('name')

      if (error) {
        console.error('Error fetching subcontractors:', error)
        return []
      }

      return data
    }
  })





  // Create assignment mutation

  // Update assignment mutation

  // Cancel assignment mutation

  // Use centralized status service for consistency
  const getStatusColor = (status: string) => {
    return getAssignmentStatusBadgeClassesV2(status as AssignmentStatusV2)
  }

  const getStatusText = (status: string) => {
    return getAssignmentStatusNameV2(status as AssignmentStatusV2)
  }

  // Filter assignments based on search term and status
  // Handle sort column click
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Filter and sort assignments
  // Helper function to get location as string
  const getLocationString = (location: string | { address: string; coordinates?: { lat: number; lng: number } }) => {
    if (typeof location === 'string') return location
    return location?.address || ''
  }

  // Extract unique cities from assignments
  const uniqueCities = Array.from(new Set(
    assignments?.map(a => {
      const loc = getLocationString(a.location)
      // Try to extract city from address (usually last part after comma)
      const parts = loc.split(',').map(p => p.trim())
      // Return the last meaningful part as city, or the whole string if no commas
      return parts.length > 1 ? parts[parts.length - 1] : loc
    }).filter(Boolean) || []
  )).sort()

  // Calculate active filter count
  const activeFilterCount = [
    dateFrom || dateTo,
    selectedStatuses.length > 0,
    selectedSubcontractors.length > 0,
    selectedTgStatuses.length > 0,
    selectedCustomerStatuses.length > 0,
    selectedCustomers.length > 0,
    selectedCities.length > 0,
    filterReadyForCustomerInvoice,
    filterReadyForTgInvoice
  ].filter(Boolean).length

  // Clear all advanced filters
  const clearAllFilters = () => {
    setDateFrom('')
    setDateTo('')
    setSelectedStatuses([])
    setSelectedSubcontractors([])
    setSelectedTgStatuses([])
    setSelectedCustomerStatuses([])
    setSelectedCustomers([])
    setSelectedCities([])
    setCustomerSearch('')
    setSubcontractorSearch('')
    setCitySearch('')
    setFilterReadyForCustomerInvoice(false)
    setFilterReadyForTgInvoice(false)
  }

  // Compute assignments ready for customer invoicing
  // Criteria: status = attesterad/slutford, customer_invoice_status = ej_fakturerad, ALL TGs have tg_status = godkand
  const assignmentsReadyForCustomerInvoice = useMemo(() => {
    return assignments?.filter(a => {
      // Status must be attesterad or slutford
      if (a.status !== 'attesterad' && a.status !== 'slutford') return false
      // Customer invoice status must be ej_fakturerad
      if (a.customer_invoice_status !== 'ej_fakturerad') return false
      // Must have at least one TG
      if (a.subcontractors.length === 0) return false
      // ALL TGs must have tg_status = godkand
      return a.subcontractors.every((s: { tg_status?: string }) => s.tg_status === 'godkand')
    }) || []
  }, [assignments])

  // Compute TG-assignment pairs ready for TG invoicing
  // Criteria: tg_status = godkand, tg_invoice_status = ej_fakturerad
  const tgAssignmentsReadyForTgInvoice = useMemo(() => {
    const result: Array<{
      id: string
      assignment_id: string
      assignment_number: number | null
      title: string
      start_date: string
      location: string | null
      tg_id: string
      tg_name: string
    }> = []
    for (const a of assignments || []) {
      for (const s of a.subcontractors) {
        if (s.tg_status === 'godkand' && s.tg_invoice_status === 'ej_fakturerad') {
          result.push({
            id: `${a.id}|${s.id}`, // Use | as separator since UUIDs contain -
            assignment_id: a.id,
            assignment_number: a.assignment_number,
            title: a.title,
            start_date: a.start_date,
            location: typeof a.location === 'string' ? a.location : (a.location as any)?.address || null,
            tg_id: s.id,
            tg_name: s.name
          })
        }
      }
    }
    return result
  }, [assignments])

  const filteredAssignments = assignments?.filter(assignment => {
    // Text search (including assignment number/ID)
    const matchesSearch = assignment.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         assignment.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         assignment.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         String(assignment.assignment_number || '').includes(searchTerm)

    // Legacy status filter (single select) - only if advanced statuses not used
    const matchesLegacyStatus = statusFilter === 'all' || assignment.status === statusFilter

    // Advanced status filter (multi-select)
    const matchesAdvancedStatus = selectedStatuses.length === 0 ||
                                  selectedStatuses.includes(assignment.status)

    // Date range filter
    const matchesDateFrom = !dateFrom || (assignment.start_date && assignment.start_date >= dateFrom)
    const matchesDateTo = !dateTo || (assignment.start_date && assignment.start_date <= dateTo)

    // Subcontractor filter (multi-select)
    const matchesSubcontractor = selectedSubcontractors.length === 0 ||
      assignment.subcontractors?.some((s: { id: string; name: string }) => selectedSubcontractors.includes(s.id))

    // TG status filter (multi-select) - filters on the selected subcontractor's tg_status if both filters are used
    const matchesTgStatus = selectedTgStatuses.length === 0 ||
      assignment.subcontractors?.some((s: { id: string; tg_status?: string }) => {
        // If subcontractor filter is active, only check those TGs
        if (selectedSubcontractors.length > 0) {
          return selectedSubcontractors.includes(s.id) && s.tg_status && selectedTgStatuses.includes(s.tg_status)
        }
        // Otherwise check all TGs on the assignment
        return s.tg_status && selectedTgStatuses.includes(s.tg_status)
      })

    // Customer filter (multi-select)
    const matchesCustomer = selectedCustomers.length === 0 ||
      selectedCustomers.includes(assignment.customer_id)

    // Customer status filter (multi-select)
    const matchesCustomerStatus = selectedCustomerStatuses.length === 0 ||
      selectedCustomerStatuses.includes(assignment.customer_comm_status)

    // City/Location filter (multi-select)
    const assignmentLocation = getLocationString(assignment.location).toLowerCase()
    const matchesCity = selectedCities.length === 0 ||
      selectedCities.some(city => assignmentLocation.includes(city.toLowerCase()))

    // Invoice filter: Ready for customer invoicing
    const matchesCustomerInvoiceFilter = !filterReadyForCustomerInvoice || (
      (assignment.status === 'attesterad' || assignment.status === 'slutford') &&
      assignment.customer_invoice_status === 'ej_fakturerad' &&
      assignment.subcontractors.length > 0 &&
      assignment.subcontractors.every((s: { tg_status?: string }) => s.tg_status === 'godkand')
    )

    // Invoice filter: Ready for TG invoicing
    const matchesTgInvoiceFilter = !filterReadyForTgInvoice || (
      assignment.subcontractors.some((s: { tg_status?: string; tg_invoice_status?: string }) =>
        s.tg_status === 'godkand' && s.tg_invoice_status === 'ej_fakturerad'
      )
    )

    // Use advanced status filter if any advanced filters are active, otherwise use legacy
    const matchesStatus = activeFilterCount > 0 ? matchesAdvancedStatus : matchesLegacyStatus

    // Hide completed assignments by default (unless user explicitly filters for them)
    const hideCompleted = selectedStatuses.length === 0 && assignment.status === 'slutford'

    return !hideCompleted && matchesSearch && matchesStatus && matchesDateFrom && matchesDateTo &&
           matchesSubcontractor && matchesTgStatus && matchesCustomer && matchesCustomerStatus &&
           matchesCity && matchesCustomerInvoiceFilter && matchesTgInvoiceFilter
  }).sort((a, b) => {
    let aValue: any = a[sortField as keyof Assignment]
    let bValue: any = b[sortField as keyof Assignment]

    // Handle null/undefined values
    if (aValue == null) aValue = ''
    if (bValue == null) bValue = ''

    // Handle numeric fields
    if (sortField === 'assignment_number') {
      aValue = aValue || 0
      bValue = bValue || 0
    }

    // Compare values
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
    return 0
  }) || []

  // Limit displayed assignments
  const displayedAssignments = itemsPerPage === -1
    ? filteredAssignments
    : filteredAssignments.slice(0, itemsPerPage)

  // Navigate to assignment detail with filtered list context
  const navigateToAssignment = (assignmentId: string) => {
    // Store the filtered assignment IDs for navigation on detail page
    const filteredIds = filteredAssignments.map(a => a.id)
    sessionStorage.setItem('filteredAssignmentIds', JSON.stringify(filteredIds))
    navigate(`/admin/assignments/${assignmentId}`)
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-bold text-nature-900 dark:text-nature-100">Uppdrag</h1>
        <div className="flex gap-2 w-full sm:w-auto">
          <button
            onClick={() => navigate('/admin/assignments/new')}
            className="btn-primary flex items-center justify-center space-x-2 flex-1 sm:flex-initial"
          >
            <Plus className="h-4 w-4" />
            <span>Lägg till</span>
          </button>
          <button
            onClick={() => navigate('/admin/assignments/bulk-create')}
            className="btn-secondary flex items-center justify-center space-x-2 flex-1 sm:flex-initial"
          >
            <Layers className="h-4 w-4" />
            <span>Skapa serie</span>
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Sök uppdrag..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field pl-10 w-full"
          />
        </div>
        {/* Quick week filters */}
        <div className="flex items-center gap-1">
          {([
            { label: 'Förra v.', offset: -1 },
            { label: 'Denna v.', offset: 0 },
            { label: 'Nästa v.', offset: 1 },
          ] as const).map(({ label, offset }) => {
            const weekBase = offset === 0 ? new Date() : offset < 0 ? subWeeks(new Date(), 1) : addWeeks(new Date(), 1)
            const wStart = format(startOfWeek(weekBase, { weekStartsOn: 1 }), 'yyyy-MM-dd')
            const wEnd = format(endOfWeek(weekBase, { weekStartsOn: 1 }), 'yyyy-MM-dd')
            const isActive = dateFrom === wStart && dateTo === wEnd
            return (
              <button
                key={offset}
                onClick={() => {
                  if (isActive) {
                    setDateFrom('')
                    setDateTo('')
                  } else {
                    setDateFrom(wStart)
                    setDateTo(wEnd)
                  }
                }}
                className={`px-2.5 py-1.5 text-xs font-medium rounded-md border transition-colors whitespace-nowrap ${
                  isActive
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'border-nature-300 dark:border-nature-600 bg-[#faf9f7] dark:bg-nature-700 text-nature-600 dark:text-nature-300 hover:bg-nature-100 dark:hover:bg-nature-600'
                }`}
              >
                {label}
              </button>
            )
          })}
        </div>
        {/* Filters row */}
        <div className="flex items-center gap-2">
          {/* Advanced filters toggle */}
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm border rounded-md transition-colors ${
              activeFilterCount > 0
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                : 'border-nature-300 dark:border-nature-600 bg-[#faf9f7] dark:bg-nature-700 text-nature-700 dark:text-nature-200 hover:bg-nature-50 dark:hover:bg-nature-600'
            }`}
          >
            <Filter className="h-4 w-4" />
            <span className="hidden sm:inline">Avancerade filter</span>
            {activeFilterCount > 0 && (
              <span className="px-1.5 py-0.5 text-xs font-medium bg-primary-500 text-white rounded-full">
                {activeFilterCount}
              </span>
            )}
            {showAdvancedFilters ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
          <select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className="px-2 py-2 text-sm border border-nature-300 dark:border-nature-600 rounded-md bg-[#faf9f7] dark:bg-nature-700 text-nature-900 dark:text-nature-100"
          >
            <option value={10}>10 st</option>
            <option value={20}>20 st</option>
            <option value={50}>50 st</option>
            <option value={100}>100 st</option>
            <option value={-1}>Alla</option>
          </select>
          <span className="text-xs text-nature-500 dark:text-nature-400 whitespace-nowrap">
            ({filteredAssignments.length})
          </span>
        </div>
      </div>

      {/* Advanced Filters Panel */}
      {showAdvancedFilters && (
        <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {/* Date Range */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Datum
                </label>
                {(dateFrom || dateTo) && (
                  <button
                    onClick={() => { setDateFrom(''); setDateTo(''); }}
                    className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 flex items-center gap-0.5"
                  >
                    <X className="h-3 w-3" />
                    Rensa
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  placeholder="Från"
                  className="input-field text-sm flex-1"
                />
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  placeholder="Till"
                  className="input-field text-sm flex-1"
                />
              </div>
            </div>

            {/* Status Multi-select */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300">
                  Status
                  {selectedStatuses.length > 0 && (
                    <span className="ml-1 text-xs text-primary-600">({selectedStatuses.length})</span>
                  )}
                </label>
                {selectedStatuses.length > 0 && (
                  <button
                    onClick={() => setSelectedStatuses([])}
                    className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 flex items-center gap-0.5"
                  >
                    <X className="h-3 w-3" />
                    Rensa
                  </button>
                )}
              </div>
              <div className="max-h-40 overflow-y-auto space-y-1 border border-nature-200 dark:border-nature-600 rounded-md p-2 bg-white dark:bg-nature-700">
                {ASSIGNMENT_STATUSES_V2.map(status => (
                  <label key={status.key} className="flex items-center gap-2 cursor-pointer hover:bg-nature-50 dark:hover:bg-nature-600 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={selectedStatuses.includes(status.key)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedStatuses([...selectedStatuses, status.key])
                        } else {
                          setSelectedStatuses(selectedStatuses.filter(s => s !== status.key))
                        }
                      }}
                      className="h-4 w-4 text-primary-600 rounded border-nature-300"
                    />
                    <span className={`text-xs px-1.5 py-0.5 rounded ${getAssignmentStatusBadgeClassesV2(status.key)}`}>
                      {status.name}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Subcontractor Multi-select */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300">
                  <Leaf className="inline h-4 w-4 mr-1" />
                  Trädgårdsmästare
                  {selectedSubcontractors.length > 0 && (
                    <span className="ml-1 text-xs text-primary-600">({selectedSubcontractors.length})</span>
                  )}
                </label>
                {selectedSubcontractors.length > 0 && (
                  <button
                    onClick={() => { setSelectedSubcontractors([]); setSubcontractorSearch(''); }}
                    className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 flex items-center gap-0.5"
                  >
                    <X className="h-3 w-3" />
                    Rensa
                  </button>
                )}
              </div>
              <input
                type="text"
                placeholder="Sök..."
                value={subcontractorSearch}
                onChange={(e) => setSubcontractorSearch(e.target.value)}
                className="input-field text-sm w-full mb-1"
              />
              <div className="max-h-32 overflow-y-auto space-y-1 border border-nature-200 dark:border-nature-600 rounded-md p-2 bg-white dark:bg-nature-700">
                {subcontractors
                  .filter(s => s.name.toLowerCase().includes(subcontractorSearch.toLowerCase()))
                  .map(sub => (
                    <label key={sub.id} className="flex items-center gap-2 cursor-pointer hover:bg-nature-50 dark:hover:bg-nature-600 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={selectedSubcontractors.includes(sub.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedSubcontractors([...selectedSubcontractors, sub.id])
                          } else {
                            setSelectedSubcontractors(selectedSubcontractors.filter(id => id !== sub.id))
                          }
                        }}
                        className="h-4 w-4 text-primary-600 rounded border-nature-300"
                      />
                      <span className="text-sm text-nature-700 dark:text-nature-200">{sub.name}</span>
                    </label>
                  ))}
              </div>
            </div>

            {/* Trädgårdsmästarstatus Multi-select */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300">
                  <FileText className="inline h-4 w-4 mr-1" />
                  Trädgårdsmästarstatus
                  {selectedTgStatuses.length > 0 && (
                    <span className="ml-1 text-xs text-primary-600">({selectedTgStatuses.length})</span>
                  )}
                </label>
                {selectedTgStatuses.length > 0 && (
                  <button
                    onClick={() => setSelectedTgStatuses([])}
                    className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 flex items-center gap-0.5"
                  >
                    <X className="h-3 w-3" />
                    Rensa
                  </button>
                )}
              </div>
              <div className="space-y-1 border border-nature-200 dark:border-nature-600 rounded-md p-2 bg-white dark:bg-nature-700">
                {[
                  { value: 'ej_rapporterad', label: 'Ej rapporterat' },
                  { value: 'rapporterad', label: 'Rapporterat' },
                  { value: 'komplettering', label: 'Komplettering begärd' },
                  { value: 'godkand', label: 'Godkänd' }
                ].map(status => (
                  <label key={status.value} className="flex items-center gap-2 cursor-pointer hover:bg-nature-50 dark:hover:bg-nature-600 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={selectedTgStatuses.includes(status.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTgStatuses([...selectedTgStatuses, status.value])
                        } else {
                          setSelectedTgStatuses(selectedTgStatuses.filter(s => s !== status.value))
                        }
                      }}
                      className="h-4 w-4 text-primary-600 rounded border-nature-300"
                    />
                    <span className="text-sm text-nature-700 dark:text-nature-200">{status.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Customer Multi-select */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300">
                  <User className="inline h-4 w-4 mr-1" />
                  Kund
                  {selectedCustomers.length > 0 && (
                    <span className="ml-1 text-xs text-primary-600">({selectedCustomers.length})</span>
                  )}
                </label>
                {selectedCustomers.length > 0 && (
                  <button
                    onClick={() => { setSelectedCustomers([]); setCustomerSearch(''); }}
                    className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 flex items-center gap-0.5"
                  >
                    <X className="h-3 w-3" />
                    Rensa
                  </button>
                )}
              </div>
              <input
                type="text"
                placeholder="Sök..."
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                className="input-field text-sm w-full mb-1"
              />
              <div className="max-h-32 overflow-y-auto space-y-1 border border-nature-200 dark:border-nature-600 rounded-md p-2 bg-white dark:bg-nature-700">
                {customers
                  .filter(c => c.full_name.toLowerCase().includes(customerSearch.toLowerCase()))
                  .map(customer => (
                    <label key={customer.id} className="flex items-center gap-2 cursor-pointer hover:bg-nature-50 dark:hover:bg-nature-600 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={selectedCustomers.includes(customer.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCustomers([...selectedCustomers, customer.id])
                          } else {
                            setSelectedCustomers(selectedCustomers.filter(id => id !== customer.id))
                          }
                        }}
                        className="h-4 w-4 text-primary-600 rounded border-nature-300"
                      />
                      <span className="text-sm text-nature-700 dark:text-nature-200">{customer.full_name}</span>
                    </label>
                  ))}
              </div>
            </div>

            {/* Customer Status Multi-select */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300">
                  <User className="inline h-4 w-4 mr-1" />
                  Kundstatus
                  {selectedCustomerStatuses.length > 0 && (
                    <span className="ml-1 text-xs text-primary-600">({selectedCustomerStatuses.length})</span>
                  )}
                </label>
                {selectedCustomerStatuses.length > 0 && (
                  <button
                    onClick={() => setSelectedCustomerStatuses([])}
                    className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 flex items-center gap-0.5"
                  >
                    <X className="h-3 w-3" />
                    Rensa
                  </button>
                )}
              </div>
              <div className="space-y-1 border border-nature-200 dark:border-nature-600 rounded-md p-2 bg-white dark:bg-nature-700">
                {[
                  { value: 'ej_kontaktad', label: 'Ej kontaktad' },
                  { value: 'informerad', label: 'Informerad' },
                  { value: 'bekraftad', label: 'Bekräftad' },
                  { value: 'avbokad', label: 'Avbokad' }
                ].map(status => (
                  <label key={status.value} className="flex items-center gap-2 cursor-pointer hover:bg-nature-50 dark:hover:bg-nature-600 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={selectedCustomerStatuses.includes(status.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedCustomerStatuses([...selectedCustomerStatuses, status.value])
                        } else {
                          setSelectedCustomerStatuses(selectedCustomerStatuses.filter(s => s !== status.value))
                        }
                      }}
                      className="h-4 w-4 text-primary-600 rounded border-nature-300"
                    />
                    <span className="text-sm text-nature-700 dark:text-nature-200">{status.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* City/Location Filter */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300">
                  <MapPin className="inline h-4 w-4 mr-1" />
                  Ort
                  {selectedCities.length > 0 && (
                    <span className="ml-1 text-xs text-primary-600">({selectedCities.length})</span>
                  )}
                </label>
                {selectedCities.length > 0 && (
                  <button
                    onClick={() => { setSelectedCities([]); setCitySearch(''); }}
                    className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 flex items-center gap-0.5"
                  >
                    <X className="h-3 w-3" />
                    Rensa
                  </button>
                )}
              </div>
              <input
                type="text"
                placeholder="Sök..."
                value={citySearch}
                onChange={(e) => setCitySearch(e.target.value)}
                className="input-field text-sm w-full mb-1"
              />
              <div className="max-h-32 overflow-y-auto space-y-1 border border-nature-200 dark:border-nature-600 rounded-md p-2 bg-white dark:bg-nature-700">
                {uniqueCities
                  .filter(city => city.toLowerCase().includes(citySearch.toLowerCase()))
                  .map(city => (
                    <label key={city} className="flex items-center gap-2 cursor-pointer hover:bg-nature-50 dark:hover:bg-nature-600 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={selectedCities.includes(city)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCities([...selectedCities, city])
                          } else {
                            setSelectedCities(selectedCities.filter(c => c !== city))
                          }
                        }}
                        className="h-4 w-4 text-primary-600 rounded border-nature-300"
                      />
                      <span className="text-sm text-nature-700 dark:text-nature-200">{city}</span>
                    </label>
                  ))}
                {uniqueCities.filter(city => city.toLowerCase().includes(citySearch.toLowerCase())).length === 0 && (
                  <p className="text-xs text-nature-500 dark:text-nature-400 italic p-1">Inga orter hittades</p>
                )}
              </div>
            </div>
          </div>

          {/* Invoice Filters Section */}
          <div className="mt-4 pt-4 border-t border-nature-200 dark:border-nature-600">
            <h4 className="text-sm font-medium text-nature-700 dark:text-nature-300 mb-3 flex items-center gap-2">
              <Receipt className="h-4 w-4" />
              Fakturering
            </h4>
            <div className="flex flex-wrap gap-4">
              {/* Ready for Customer Invoice */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filterReadyForCustomerInvoice}
                  onChange={(e) => setFilterReadyForCustomerInvoice(e.target.checked)}
                  className="h-4 w-4 text-blue-600 rounded border-nature-300"
                />
                <span className="text-sm text-nature-700 dark:text-nature-200">
                  Redo för kundfakturering
                  {assignmentsReadyForCustomerInvoice.length > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
                      {assignmentsReadyForCustomerInvoice.length}
                    </span>
                  )}
                </span>
              </label>

              {/* Ready for TG Invoice */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filterReadyForTgInvoice}
                  onChange={(e) => setFilterReadyForTgInvoice(e.target.checked)}
                  className="h-4 w-4 text-green-600 rounded border-nature-300"
                />
                <span className="text-sm text-nature-700 dark:text-nature-200">
                  Redo för trädgårdsmästarfakturering
                  {tgAssignmentsReadyForTgInvoice.length > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full">
                      {tgAssignmentsReadyForTgInvoice.length}
                    </span>
                  )}
                </span>
              </label>
            </div>

            {/* Export buttons */}
            <div className="flex flex-wrap gap-2 mt-3">
              <button
                onClick={() => setShowCustomerExportModal(true)}
                disabled={assignmentsReadyForCustomerInvoice.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FileSpreadsheet className="h-4 w-4" />
                Exportera kundunderlag ({assignmentsReadyForCustomerInvoice.length})
              </button>

              <button
                onClick={() => setShowTgExportModal(true)}
                disabled={tgAssignmentsReadyForTgInvoice.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FileSpreadsheet className="h-4 w-4" />
                Exportera trädgårdsmästarunderlag ({tgAssignmentsReadyForTgInvoice.length})
              </button>
            </div>
          </div>

          {/* Clear filters button */}
          {activeFilterCount > 0 && (
            <div className="mt-4 pt-4 border-t border-nature-200 dark:border-nature-600 flex justify-end">
              <button
                onClick={clearAllFilters}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
              >
                <X className="h-4 w-4" />
                Rensa alla filter
              </button>
            </div>
          )}
        </div>
      )}

      {/* Assignments List */}
        <div className="bg-[#faf9f7] dark:bg-nature-800 shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-nature-600 mx-auto"></div>
            <p className="mt-2 text-nature-600">Laddar uppdrag...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8 text-red-600">
            <p>Fel vid laddning av uppdrag: {error.message}</p>
            <p className="text-sm text-gray-500 mt-2">Kontrollera att databastabellerna finns</p>
          </div>
        ) : assignments && assignments.length > 0 ? (
          <>
            {/* Mobile Card View */}
            <div className="lg:hidden divide-y divide-nature-200 dark:divide-nature-700">
              {displayedAssignments.map((assignment) => (
                  <div
                    key={assignment.id}
                    onClick={() => navigateToAssignment(assignment.id)}
                    className="p-4 cursor-pointer active:bg-nature-100 dark:active:bg-nature-700"
                  >
                    {/* Top row: Status + Date */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`badge text-xs ${getStatusColor(assignment.status)}`}>
                          {getStatusText(assignment.status)}
                        </span>
                        <span className="text-xs text-nature-500 dark:text-nature-400 font-mono">
                          #{assignment.assignment_number || '—'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {assignment.start_date && (
                          <span className="text-xs text-nature-600 dark:text-nature-300">
                            {format(new Date(assignment.start_date), 'd MMM', { locale: sv })}
                          </span>
                        )}
                        <ChevronRight className="h-4 w-4 text-nature-400" />
                      </div>
                    </div>

                    {/* Title */}
                    <div className="text-sm font-medium text-nature-900 dark:text-nature-100 mb-1">
                      {assignment.title}
                    </div>

                    {/* Customer name - clickable link */}
                    <div className="mb-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/admin/customers/${assignment.customer_id}`)
                        }}
                        className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        {assignment.customer_name}
                      </button>
                    </div>

                    {/* Address with navigation */}
                    {getLocationDisplay(assignment.location) && (
                      <div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            const coords = getLocationCoordinates(assignment.location)
                            const address = getLocationDisplay(assignment.location)
                            if (coords) {
                              window.open(`https://www.google.com/maps?q=${coords.lat},${coords.lng}`, '_blank')
                            } else if (address) {
                              window.open(`https://www.google.com/maps?q=${encodeURIComponent(address)}`, '_blank')
                            }
                          }}
                          className="text-xs text-nature-500 dark:text-nature-400 hover:text-blue-600 dark:hover:text-blue-400 flex items-center gap-1"
                        >
                          <NavigationIcon className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{getLocationDisplay(assignment.location)}</span>
                        </button>
                      </div>
                    )}

                    {/* Bottom row: Subcontractor + Day */}
                    <div className="mt-2 flex items-center justify-between">
                      <div className="text-xs text-nature-500 dark:text-nature-400">
                        {assignment.subcontractors.length > 0 ? (
                          <span className="flex items-center gap-1">
                            <Leaf className="h-3 w-3" />
                            {assignment.subcontractors.map((sub: { id: string; name: string }) => sub.name).join(', ')}
                          </span>
                        ) : (
                          <span className="italic">Ej tilldelad</span>
                        )}
                      </div>
                      {assignment.start_date && (
                        <span className="text-xs text-nature-400 dark:text-nature-500 capitalize">
                          {format(new Date(assignment.start_date), 'EEEE', { locale: sv })}
                        </span>
                      )}
                    </div>
                  </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full divide-y divide-nature-200 dark:divide-nature-700">
                <thead className="bg-nature-50 dark:bg-nature-700">
                  <tr>
                    <th
                      className="px-4 py-4 text-left text-xs font-medium text-nature-500 dark:text-nature-300 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                      onClick={() => handleSort('assignment_number')}
                    >
                      <div className="flex items-center gap-1">
                        ID
                        {sortField === 'assignment_number' ? (
                          sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 opacity-50" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-4 text-left text-xs font-medium text-nature-500 dark:text-nature-300 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center gap-1">
                        Status
                        {sortField === 'status' ? (
                          sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 opacity-50" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-4 text-left text-xs font-medium text-nature-500 dark:text-nature-300 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                      onClick={() => handleSort('title')}
                    >
                      <div className="flex items-center gap-1">
                        Uppdrag
                        {sortField === 'title' ? (
                          sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 opacity-50" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-4 text-left text-xs font-medium text-nature-500 dark:text-nature-300 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                      onClick={() => handleSort('customer_name')}
                    >
                      <div className="flex items-center gap-1">
                        Kund
                        {sortField === 'customer_name' ? (
                          sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 opacity-50" />
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left text-xs font-medium text-nature-500 dark:text-nature-300 uppercase tracking-wider">
                      Trädgårdsmästare
                    </th>
                    <th
                      className="px-4 py-4 text-left text-xs font-medium text-nature-500 dark:text-nature-300 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                      onClick={() => handleSort('start_date')}
                    >
                      <div className="flex items-center gap-1">
                        Datum
                        {sortField === 'start_date' ? (
                          sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 opacity-50" />
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-[#faf9f7] dark:bg-nature-800 divide-y divide-nature-200 dark:divide-nature-700">
                  {displayedAssignments.map((assignment) => (
                    <tr
                      key={assignment.id}
                      onClick={() => navigateToAssignment(assignment.id)}
                      className="hover:bg-nature-50 dark:hover:bg-nature-700 cursor-pointer"
                    >
                      {/* ID */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-nature-700 dark:text-nature-300 font-mono">
                          #{assignment.assignment_number || '—'}
                        </span>
                      </td>
                      {/* Status */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`badge ${getStatusColor(assignment.status)}`}>
                          {getStatusText(assignment.status)}
                        </span>
                      </td>
                      {/* Uppdrag */}
                      <td className="px-4 py-4">
                        <div>
                          <div className="text-sm font-medium text-nature-900 dark:text-nature-100">
                            {assignment.title}
                          </div>
                          {assignment.description && (
                            <div className="text-sm text-nature-500 dark:text-nature-400 truncate max-w-xs">
                              {assignment.description}
                            </div>
                          )}
                        </div>
                      </td>
                      {/* Kund */}
                      <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex flex-col">
                          <button
                            onClick={() => navigate(`/admin/customers/${assignment.customer_id}`)}
                            className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 hover:underline text-left"
                          >
                            {assignment.customer_name}
                          </button>
                          {getLocationDisplay(assignment.location) && (
                            <button
                              onClick={() => {
                                const coords = getLocationCoordinates(assignment.location)
                                const address = getLocationDisplay(assignment.location)

                                if (coords) {
                                  window.open(`https://www.google.com/maps?q=${coords.lat},${coords.lng}`, '_blank')
                                } else if (address) {
                                  window.open(`https://www.google.com/maps?q=${encodeURIComponent(address)}`, '_blank')
                                } else {
                                  toast.error('Ingen platsinformation tillgänglig')
                                }
                              }}
                              className="text-xs text-nature-500 dark:text-nature-400 hover:text-blue-600 dark:hover:text-blue-400 flex items-center gap-1 mt-0.5 text-left"
                              title="Öppna i Google Maps"
                            >
                              <NavigationIcon className="h-3 w-3" />
                              <span className="truncate max-w-[200px]">{getLocationDisplay(assignment.location)}</span>
                            </button>
                          )}
                        </div>
                      </td>
                      {/* Trädgårdsmästare */}
                      <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                        <div className="text-sm">
                          {assignment.subcontractors.length > 0 ? (
                            <div className="flex flex-col gap-0.5">
                              {assignment.subcontractors.map((sub: { id: string; name: string }) => (
                                <button
                                  key={sub.id}
                                  onClick={() => navigate(`/admin/subcontractors/${sub.id}`)}
                                  className="text-left text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 hover:underline"
                                >
                                  {sub.name}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <span className="text-nature-400 dark:text-nature-500 italic">Ej tilldelad</span>
                          )}
                        </div>
                      </td>
                      {/* Datum */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        {assignment.start_date ? (
                          <div>
                            <div className="text-sm text-nature-900 dark:text-nature-100">
                              {format(new Date(assignment.start_date), 'yyyy-MM-dd', { locale: sv })}
                            </div>
                            <div className="text-xs text-nature-500 dark:text-nature-400 capitalize">
                              {format(new Date(assignment.start_date), 'EEEE', { locale: sv })}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-nature-400 dark:text-nature-500 italic">
                            Ej planerad
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="text-center py-8 text-nature-500 dark:text-nature-400">
            <p>Inga uppdrag hittades</p>
            <p className="text-sm">Skapa ditt första uppdrag genom att klicka på "Lägg till"</p>
          </div>
        )}
        </div>

      {/* Customer Invoice Export Modal */}
      <ExportCustomerInvoiceModal
        isOpen={showCustomerExportModal}
        onClose={() => setShowCustomerExportModal(false)}
        assignments={assignmentsReadyForCustomerInvoice.map(a => ({
          id: a.id,
          assignment_number: a.assignment_number,
          title: a.title,
          start_date: a.start_date,
          customer_id: a.customer_id,
          customer_name: a.customer_name,
          location: typeof a.location === 'string' ? a.location : (a.location as any)?.address || null
        }))}
        onExportComplete={() => {
          queryClient.invalidateQueries({ queryKey: ['assignments'] })
        }}
      />

      {/* TG Invoice Export Modal */}
      <ExportTgInvoiceAdminModal
        isOpen={showTgExportModal}
        onClose={() => setShowTgExportModal(false)}
        tgAssignments={tgAssignmentsReadyForTgInvoice}
        onExportComplete={() => {
          queryClient.invalidateQueries({ queryKey: ['assignments'] })
        }}
      />
    </div>
  )
}
